import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Alert,
  InputAdornment
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import loansService, { Loan, CreateLoanRequest, UpdateLoanRequest } from '../../services/loansService';
import { clientService } from '../../services/clientService';
import { Client } from '../../types/clients';

interface LoanFormProps {
  open: boolean;
  onClose: () => void;
  loan?: Loan | null; // If provided, we're editing; otherwise creating
  clientId?: string; // Pre-selected client
}

const loanSchema = yup.object({
  clientId: yup.string().required('Client is required'),
  type: yup.string().required('Loan type is required'),
  purpose: yup.string().required('Loan purpose is required'),
  requestedAmount: yup.number()
    .required('Requested amount is required')
    .min(1000, 'Minimum loan amount is $1,000')
    .max(10000000, 'Maximum loan amount is $10,000,000'),
  approvedAmount: yup.number()
    .min(0, 'Approved amount must be positive')
    .optional(),
  interestRate: yup.number()
    .min(0, 'Interest rate must be positive')
    .max(100, 'Interest rate cannot exceed 100%')
    .optional(),
  termMonths: yup.number()
    .required('Term is required')
    .min(1, 'Minimum term is 1 month')
    .max(360, 'Maximum term is 360 months'),
  paymentFrequency: yup.string().optional(),
  paymentAmount: yup.number().min(0, 'Payment amount must be positive').optional(),
  collateralType: yup.string().optional(),
  collateralDescription: yup.string().optional(),
  collateralValue: yup.number().min(0, 'Collateral value must be positive').optional(),
  underwritingNotes: yup.string().optional()
});

type LoanFormData = yup.InferType<typeof loanSchema>;

const LoanForm: React.FC<LoanFormProps> = ({
  open,
  onClose,
  loan,
  clientId
}) => {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const isEditing = !!loan;

  const { data: clientsData } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientService.listClients(),
    enabled: !clientId // Only fetch if no client is pre-selected
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
    setValue
  } = useForm<LoanFormData>({
    resolver: yupResolver(loanSchema),
    mode: 'onBlur',
    defaultValues: {
      clientId: clientId || loan?.clientId || '',
      type: loan?.type || 'Term Loan',
      purpose: loan?.purpose || 'Working Capital',
      requestedAmount: loan?.requestedAmount || 0,
      approvedAmount: loan?.approvedAmount || undefined,
      interestRate: loan?.interestRate || undefined,
      termMonths: loan?.termMonths || 12,
      paymentFrequency: loan?.paymentFrequency || 'Monthly',
      paymentAmount: loan?.paymentAmount || undefined,
      collateralType: loan?.collateral?.type || 'None',
      collateralDescription: loan?.collateral?.description || '',
      collateralValue: loan?.collateral?.value || undefined,
      underwritingNotes: loan?.underwritingNotes || ''
    }
  });

  const watchedValues = watch(['requestedAmount', 'interestRate', 'termMonths', 'paymentFrequency']);

  // Calculate monthly payment when relevant fields change
  useEffect(() => {
    const [amount, rate, term] = watchedValues;
    if (amount && rate && term) {
      const monthlyPayment = loansService.calculateMonthlyPayment(amount, rate, term);
      setValue('paymentAmount', monthlyPayment);
    }
  }, watchedValues);

  const createLoanMutation = useMutation({
    mutationFn: (data: CreateLoanRequest) => loansService.createLoan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      onClose();
      reset();
    },
    onError: (error: unknown) => {
      setSubmitError(error instanceof Error ? error.message : 'Failed to create loan');
    }
  });

  const updateLoanMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLoanRequest }) => 
      loansService.updateLoan(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      onClose();
    },
    onError: (error: unknown) => {
      setSubmitError(error instanceof Error ? error.message : 'Failed to update loan');
    }
  });

  const onSubmit = (data: LoanFormData) => {
    setSubmitError(null);

    const loanData = {
      clientId: data.clientId,
      type: data.type as Loan['type'],
      purpose: data.purpose as Loan['purpose'],
      requestedAmount: data.requestedAmount,
      termMonths: data.termMonths,
      ...(data.approvedAmount && { approvedAmount: data.approvedAmount }),
      ...(data.interestRate && { interestRate: data.interestRate }),
      ...(data.paymentFrequency && { paymentFrequency: data.paymentFrequency as Loan['paymentFrequency'] }),
      ...(data.paymentAmount && { paymentAmount: data.paymentAmount }),
      ...(data.collateralType && data.collateralType !== 'None' && {
        collateral: {
          type: data.collateralType as 'Real Estate' | 'Equipment' | 'Inventory' | 'Accounts Receivable' | 'Personal Guarantee',
          description: data.collateralDescription || undefined,
          value: data.collateralValue || undefined
        }
      }),
      ...(data.underwritingNotes && { underwritingNotes: data.underwritingNotes })
    };

    if (isEditing && loan) {
      updateLoanMutation.mutate({ id: loan.id, data: loanData });
    } else {
      createLoanMutation.mutate(loanData as CreateLoanRequest);
    }
  };

  const handleClose = () => {
    reset();
    setSubmitError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditing ? `Edit Loan ${loan?.loanNumber}` : 'Create New Loan'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Client Selection */}
            <Grid size={12}>
              <Controller
                name="clientId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.clientId} disabled={!!clientId}>
                    <InputLabel>Client *</InputLabel>
                    <Select {...field} label="Client *">
                      {clientsData?.clients.map((client: Client) => (
                        <MenuItem key={client.id} value={client.id}>
                          {client.businessName}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.clientId && (
                      <Typography variant="caption" color="error">
                        {errors.clientId.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Loan Basic Info */}
            <Grid size={12}>
              <Typography variant="h6" gutterBottom>Loan Details</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.type}>
                    <InputLabel>Loan Type *</InputLabel>
                    <Select {...field} label="Loan Type *">
                      <MenuItem value="Term Loan">Term Loan</MenuItem>
                      <MenuItem value="Line of Credit">Line of Credit</MenuItem>
                      <MenuItem value="Equipment Financing">Equipment Financing</MenuItem>
                      <MenuItem value="SBA Loan">SBA Loan</MenuItem>
                      <MenuItem value="Merchant Cash Advance">Merchant Cash Advance</MenuItem>
                      <MenuItem value="Invoice Factoring">Invoice Factoring</MenuItem>
                      <MenuItem value="Real Estate Loan">Real Estate Loan</MenuItem>
                    </Select>
                    {errors.type && (
                      <Typography variant="caption" color="error">
                        {errors.type.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="purpose"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.purpose}>
                    <InputLabel>Purpose *</InputLabel>
                    <Select {...field} label="Purpose *">
                      <MenuItem value="Working Capital">Working Capital</MenuItem>
                      <MenuItem value="Equipment Purchase">Equipment Purchase</MenuItem>
                      <MenuItem value="Real Estate">Real Estate</MenuItem>
                      <MenuItem value="Inventory">Inventory</MenuItem>
                      <MenuItem value="Business Expansion">Business Expansion</MenuItem>
                      <MenuItem value="Debt Consolidation">Debt Consolidation</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                    {errors.purpose && (
                      <Typography variant="caption" color="error">
                        {errors.purpose.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="requestedAmount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Requested Amount *"
                    type="number"
                    error={!!errors.requestedAmount}
                    helperText={errors.requestedAmount?.message}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="termMonths"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Term (Months) *"
                    type="number"
                    error={!!errors.termMonths}
                    helperText={errors.termMonths?.message}
                  />
                )}
              />
            </Grid>

            {/* Approved Terms (for editing) */}
            {isEditing && (
              <>
                <Grid size={12}>
                  <Typography variant="h6" gutterBottom>Approved Terms</Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="approvedAmount"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Approved Amount"
                        type="number"
                        error={!!errors.approvedAmount}
                        helperText={errors.approvedAmount?.message}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="interestRate"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Interest Rate"
                        type="number"
                        error={!!errors.interestRate}
                        helperText={errors.interestRate?.message}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="paymentFrequency"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Payment Frequency</InputLabel>
                        <Select {...field} label="Payment Frequency">
                          <MenuItem value="Weekly">Weekly</MenuItem>
                          <MenuItem value="Bi-weekly">Bi-weekly</MenuItem>
                          <MenuItem value="Monthly">Monthly</MenuItem>
                          <MenuItem value="Quarterly">Quarterly</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="paymentAmount"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Payment Amount"
                        type="number"
                        error={!!errors.paymentAmount}
                        helperText={errors.paymentAmount?.message}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    )}
                  />
                </Grid>
              </>
            )}

            {/* Collateral */}
            <Grid size={12}>
              <Typography variant="h6" gutterBottom>Collateral</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="collateralType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Collateral Type</InputLabel>
                    <Select {...field} label="Collateral Type">
                      <MenuItem value="None">None</MenuItem>
                      <MenuItem value="Real Estate">Real Estate</MenuItem>
                      <MenuItem value="Equipment">Equipment</MenuItem>
                      <MenuItem value="Inventory">Inventory</MenuItem>
                      <MenuItem value="Accounts Receivable">Accounts Receivable</MenuItem>
                      <MenuItem value="Personal Guarantee">Personal Guarantee</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="collateralValue"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Collateral Value"
                    type="number"
                    error={!!errors.collateralValue}
                    helperText={errors.collateralValue?.message}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="collateralDescription"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Collateral Description"
                    multiline
                    rows={2}
                    placeholder="Describe the collateral in detail..."
                  />
                )}
              />
            </Grid>

            {/* Notes */}
            <Grid size={12}>
              <Controller
                name="underwritingNotes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Underwriting Notes"
                    multiline
                    rows={3}
                    placeholder="Add any additional notes about this loan..."
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid || createLoanMutation.isPending || updateLoanMutation.isPending}
          >
            {createLoanMutation.isPending || updateLoanMutation.isPending
              ? 'Saving...'
              : isEditing
              ? 'Update Loan'
              : 'Create Loan'
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default LoanForm;
